import {
  updateDocument,
  createDocument,
  getId,
  saveHasOneRelationship
} from 'services/firestore'

import { updateParentHasManyRelationship } from 'hooks'
import { Application } from 'models'
import { message } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useTranslations } from 'contexts/Translation'

const useActionsApplicationAdvancedForm = ({ initialData, form } = {}) => {
  // [COMPONENT_STATE_HOOKS]
  const [loading, setLoading] = useState(false)

  // [ADDITIONAL_HOOKS]
  const { t } = useTranslations()
  const history = useHistory()
  const params = useParams()

  const prepareValues = async (values = {}, additionalValues = {}) => {
    const applicationId =
      initialData?.['application']?._id || getId('applications')
    const additionalReferences = { applicationId }
    const preparedValues = {
      _id: applicationId,
      ...additionalValues,
      name: values?.['application']?.['name'] ?? null,
      pageIds: values?.['application']?.['pageIds'] ?? null,
      platformId: await saveHasOneRelationship(
        'platforms',
        values['platformId'],
        additionalReferences
      ),
      roleId: await saveHasOneRelationship(
        'roles',
        values['roleId'],
        additionalReferences
      )
    }
    if (params.customerId) preparedValues['customerId'] = params.customerId
    return preparedValues
  }

  const saveForm = async (values, callback) => {
    try {
      // Prepare data to be saved
      const data = await prepareValues(values)
      // Updating parent references
      if (params.customerId)
        await updateParentHasManyRelationship(
          params.customerId,
          'customers',
          'applicationIds',
          data._id
        )
      // Save data
      if (initialData) {
        await updateDocument('applications', initialData.application?._id, data)
        message.success(t('Application successfully updated'))
      } else {
        await createDocument('applications', data, data._id)
        message.success(t('Application successfully created'))
      }
      // Final callback
      callback?.()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Something went wrong during data save'))
    }
  }

  const validateForm = (values) => {
    try {
      // Prepare data to be validated
      const validationData = {
        name: values?.['application']?.['name'],
        pageIds: values?.['application']?.['pageIds']
      }
      Application.validationSchema.validateSync(validationData)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Application validation error: ') + t(error.message))
    }
  }

  const onFinish = async () => {
    if (loading) return // Avoid multiple calls

    try {
      setLoading(true)
      // Get form values
      const formValues = form.getFieldsValue()
      // Validate fields
      validateForm(formValues)
      // Final callback
      const callback = () => history.goBack()
      // Save data
      await saveForm(formValues, callback)
    } catch (error) {
      setLoading(false)
      message.error(error.message)
    }
  }

  const onReset = () => {
    form.resetFields()
    history.goBack()
  }

  return { onFinish, onReset, loading, saveForm, validateForm, prepareValues }
}

export default useActionsApplicationAdvancedForm
